import { useState, useEffect } from 'react';
import { personnelApi, Personnel } from '../../api/personnel.api';
import { departmentsApi, Department } from '../../api/departments.api';
import { branchesApi, Branch } from '../../api/branches.api';
import { companiesApi, Company } from '../../api/companies.api';
import Table, { Column } from '../../components/common/Table';
import Button from '../../components/forms/Button';
import Loading from '../../components/common/Loading';
import Modal from '../../components/common/Modal';
import Input from '../../components/forms/Input';
import Select from '../../components/forms/Select';

const PersonnelList = () => {
  const [personnel, setPersonnel] = useState<Personnel[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeviceModalOpen, setIsDeviceModalOpen] = useState(false);
  const [selectedPersonnel, setSelectedPersonnel] = useState<Personnel | null>(null);
  const [personnelDevices, setPersonnelDevices] = useState<any[]>([]);
  const [editingPersonnel, setEditingPersonnel] = useState<Personnel | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterCompany, setFilterCompany] = useState('all');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    department: '',
    company: '',
    branch: '',
    phone: '',
    email: '',
    address: '',
  });

  useEffect(() => {
    fetchDepartments();
    fetchBranches();
    fetchCompanies();
  }, []);

  useEffect(() => {
    // Debounce search - wait 300ms after user stops typing
    const timer = setTimeout(() => {
      fetchPersonnel();
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, filterDepartment, filterCompany]);

  const fetchPersonnel = async () => {
    try {
      setIsLoading(true);
      const data = await personnelApi.getAll(
        searchTerm.length >= 3 ? searchTerm : undefined,
        filterDepartment !== 'all' ? filterDepartment : undefined,
        filterCompany !== 'all' ? filterCompany : undefined,
      );
      setPersonnel(data);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Personel listesi yüklenirken hata oluştu');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const data = await departmentsApi.getAll();
      setDepartments(data.filter((d) => d.isActive));
    } catch (err: any) {
      console.error('Departmanlar yüklenirken hata:', err);
    }
  };

  const fetchBranches = async () => {
    try {
      const data = await branchesApi.getAll();
      setBranches(data.filter((b) => b.isActive));
    } catch (err: any) {
      console.error('Şubeler yüklenirken hata:', err);
    }
  };

  const fetchCompanies = async () => {
    try {
      const data = await companiesApi.getAll();
      setCompanies(data.filter((c) => c.isActive));
    } catch (err: any) {
      console.error('Şirketler yüklenirken hata:', err);
    }
  };

  const handleOpenModal = (person?: Personnel) => {
    if (person) {
      setEditingPersonnel(person);
      
      // Extract IDs from populated objects if needed
      const departmentId = typeof person.department === 'object' && person.department !== null
        ? (person.department as any)._id || (person.department as any).id
        : person.department;
      
      const branchId = typeof person.branch === 'object' && person.branch !== null
        ? (person.branch as any)._id || (person.branch as any).id
        : person.branch;
      
      const companyId = person.company && typeof person.company === 'object' && person.company !== null
        ? (person.company as any)._id || (person.company as any).id
        : person.company;
      
      setFormData({
        firstName: person.firstName,
        lastName: person.lastName,
        department: departmentId,
        company: companyId || '',
        branch: branchId,
        phone: person.phone || '',
        email: person.email || '',
        address: person.address || '',
      });
    } else {
      setEditingPersonnel(null);
      setFormData({
        firstName: '',
        lastName: '',
        department: '',
        company: '',
        branch: '',
        phone: '',
        email: '',
        address: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPersonnel(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Prepare data - remove empty/undefined fields
      const submitData: any = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        department: formData.department,
        branch: formData.branch,
        phone: formData.phone,
        email: formData.email,
        address: formData.address,
      };
      
      // Only include company if it has a value
      if (formData.company && formData.company.trim() !== '') {
        submitData.company = formData.company;
      }
      
      if (editingPersonnel) {
        await personnelApi.update(editingPersonnel.id, submitData);
      } else {
        await personnelApi.create(submitData);
      }
      await fetchPersonnel();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız oldu');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bu personeli silmek istediğinizden emin misiniz?')) {
      return;
    }
    try {
      await personnelApi.delete(id);
      await fetchPersonnel();
    } catch (err: any) {
      setError(err.message || 'Silme işlemi başarısız oldu');
    }
  };

  const handleViewDevices = async (person: Personnel) => {
    try {
      setSelectedPersonnel(person);
      const data = await personnelApi.getPersonnelDevices(person.id);
      setPersonnelDevices(data.devices || []);
      setIsDeviceModalOpen(true);
    } catch (err: any) {
      setError(err.message || 'Cihazlar yüklenirken hata oluştu');
    }
  };

  const handleCloseDeviceModal = () => {
    setIsDeviceModalOpen(false);
    setSelectedPersonnel(null);
    setPersonnelDevices([]);
  };

  const getBranchName = (branch: any): string => {
    if (typeof branch === 'object' && branch !== null) {
      return branch.name || '-';
    }
    const branchObj = branches.find((b) => b.id === branch);
    return branchObj?.name || '-';
  };

  const getDepartmentName = (department: any): string => {
    if (typeof department === 'object' && department !== null) {
      return department.name || '-';
    }
    const dept = departments.find((d) => d.id === department);
    return dept?.name || '-';
  };

  const getCompanyName = (company: any): string => {
    if (!company) return '-';
    if (typeof company === 'object' && company !== null) {
      return company.name || '-';
    }
    const comp = companies.find((c) => c.id === company);
    return comp?.name || '-';
  };

  const columns: Column<Personnel>[] = [
    {
      key: 'name',
      header: 'Ad Soyad',
      render: (item) => `${item.firstName} ${item.lastName}`,
    },
    {
      key: 'branch',
      header: 'Şube',
      render: (item) => getBranchName(item.branch),
    },
    {
      key: 'department',
      header: 'Departman',
      render: (item) => getDepartmentName(item.department),
    },
    {
      key: 'company',
      header: 'Şirket',
      render: (item) => getCompanyName(item.company),
    },
    { key: 'phone', header: 'Telefon', render: (item) => item.phone || '-' },
    { key: 'email', header: 'E-posta', render: (item) => item.email || '-' },
    {
      key: 'deviceCount',
      header: 'Envanter',
      render: (item) => (
        <button
          onClick={() => handleViewDevices(item)}
          className="text-blue-600 hover:text-blue-800 font-semibold"
        >
          {item.deviceCount || 0} Cihaz
        </button>
      ),
    },
    {
      key: 'isActive',
      header: 'Durum',
      render: (item) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-semibold ${
            item.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {item.isActive ? 'Aktif' : 'Pasif'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'İşlemler',
      render: (item) => (
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => handleOpenModal(item)}>
            Düzenle
          </Button>
          <Button size="sm" variant="danger" onClick={() => handleDelete(item.id)}>
            Sil
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading) {
    return <Loading text="Personel listesi yükleniyor..." />;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Personel</h1>
        <Button onClick={() => handleOpenModal()}>Yeni Personel Ekle</Button>
      </div>

      <div className="mb-4 grid grid-cols-1 md:grid-cols-3 gap-4">
        <Input
          placeholder="Ad, soyad, email veya telefon ile ara (min 3 karakter)..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Select
          value={filterDepartment}
          onChange={(value) => setFilterDepartment(value)}
          options={[
            { value: 'all', label: 'Tüm Departmanlar' },
            ...departments.map((d) => ({ value: d.id, label: d.name })),
          ]}
          placeholder="Departman Seçiniz"
        />
        <Select
          value={filterCompany}
          onChange={(value) => setFilterCompany(value)}
          options={[
            { value: 'all', label: 'Tüm Şirketler' },
            ...companies.map((c) => ({ value: c.id, label: c.name })),
          ]}
          placeholder="Şirket Seçiniz"
        />
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">
          {error}
        </div>
      )}

      <Table data={personnel} columns={columns} emptyMessage="Henüz personel bulunmamaktadır" />

      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingPersonnel ? 'Personel Düzenle' : 'Yeni Personel Ekle'}
        size="lg"
      >
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Ad"
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
              required
            />
            <Input
              label="Soyad"
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
              required
            />
          </div>
          <Select
            label="Şube"
            value={formData.branch}
            onChange={(value) => setFormData({ ...formData, branch: value })}
            options={branches.map((b) => ({ value: b.id, label: b.name }))}
            placeholder="Şube seçiniz"
            required
          />
          <Select
            label="Departman"
            value={formData.department}
            onChange={(value) => setFormData({ ...formData, department: value })}
            options={departments.map((d) => ({ value: d.id, label: d.name }))}
            placeholder="Departman seçiniz"
            required
          />
          <Select
            label="Şirket"
            value={formData.company}
            onChange={(value) => setFormData({ ...formData, company: value })}
            options={companies.map((c) => ({ value: c.id, label: c.name }))}
            placeholder="Şirket seçiniz (opsiyonel)"
          />
          <Input
            label="Telefon"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
          <Input
            label="E-posta"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <Input
            label="Adres"
            value={formData.address}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
          />
          <div className="flex gap-2 mt-6">
            <Button type="submit" fullWidth>
              {editingPersonnel ? 'Güncelle' : 'Ekle'}
            </Button>
            <Button type="button" variant="outline" fullWidth onClick={handleCloseModal}>
              İptal
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isDeviceModalOpen}
        onClose={handleCloseDeviceModal}
        title={`${selectedPersonnel?.firstName} ${selectedPersonnel?.lastName} - Zimmetli Cihazlar`}
        size="lg"
      >
        {personnelDevices.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Bu personele atanmış cihaz bulunmamaktadır.
          </div>
        ) : (
          <div className="space-y-4">
            {personnelDevices.map((device: any) => (
              <div
                key={device._id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50"
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">
                      {device.product?.name || device.name || device.serialNumber}
                    </h4>
                    <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span className="text-gray-600">Seri No:</span>
                        <span className="ml-2 text-gray-900">{device.serialNumber}</span>
                      </div>
                      {(device.product?.brand || device.brand) && (
                        <div>
                          <span className="text-gray-600">Marka:</span>
                          <span className="ml-2 text-gray-900">
                            {typeof device.product?.brand === 'object' 
                              ? device.product.brand.name 
                              : device.product?.brand || device.brand}
                          </span>
                        </div>
                      )}
                      {(device.product?.model || device.model) && (
                        <div>
                          <span className="text-gray-600">Model:</span>
                          <span className="ml-2 text-gray-900">
                            {device.product?.model || device.model}
                          </span>
                        </div>
                      )}
                      {device.category && (
                        <div>
                          <span className="text-gray-600">Kategori:</span>
                          <span className="ml-2 text-gray-900">
                            {typeof device.category === 'object' 
                              ? device.category.name 
                              : device.category}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600">Durum:</span>
                        <span className="ml-2">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-semibold ${
                              device.status === 'Assigned'
                                ? 'bg-blue-100 text-blue-800'
                                : device.status === 'Available'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {device.status === 'Assigned' 
                              ? 'Zimmetli' 
                              : device.status === 'Available'
                              ? 'Müsait'
                              : device.status}
                          </span>
                        </span>
                      </div>
                      {device.condition && (
                        <div>
                          <span className="text-gray-600">Kondisyon:</span>
                          <span className="ml-2 text-gray-900">{device.condition}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PersonnelList;
